import { PtLuxonDate } from '../../../../models/projects/date.models';
import { MissingFieldItem } from './jacking-missing-fields.helper';
import {JackingFormInstanceEditor} from '../../../../models/jacking-v2/jacking.models';

export function buildMissingCustom(editor: any): MissingFieldItem[] {
  const items: MissingFieldItem[] = [];

  if (isSectionEnabled(editor, 'JackingEvents')) {
    items.push(...getMissingEventTimes(editor));
    items.push(...getMissingFinalDataAndPosition(editor));
  }

  if (isSectionEnabled(editor, 'LegSummary')) {
    items.push(...getMissingLegSummary(editor));
  }

  if (isSectionEnabled(editor, 'Signatures')) {
    items.push(...getMissingSignatures(editor));
  }

  return items;
}


function getSectionPageOrder(editor: any, sectionCode: string, fallback: number): number {
  const po = editor?.template?.sections?.find((s: any) => s.code === sectionCode)?.pageOrder;
  return po != null ? po : fallback;
}

function getSection(editor: any, code: string): any | null {
  return editor?.template?.sections?.find((s: any) => s.code === code) ?? null;
}

function isSectionEnabled(editor: any, code: string): boolean {
  const s = getSection(editor, code);
  if (!s) {
    return false;
  }

  return s.enabled === true;
}

function findGroupByComponent(editor: JackingFormInstanceEditor, componentKey: string): any | null {
  const sections = editor?.template?.sections || [];
  for (const s of sections) {
    const groups = s?.groups || [];
    for (const g of groups) {
      if (g?.componentKey === componentKey) {
        return g;
      }
    }
  }
  return null;
}

function createMissingFromGroup(group: any, label: string): MissingFieldItem {
  return {
    sectionName: group?.sectionName || '',
    groupName: group?.title || '',
    fieldLabel: label,
    fieldTemplateId: -group?.id,

    sectionPageOrder: group?.sectionPageOrder,
    groupDisplayOrder: group?.displayOrder
  } as any;
}


function getMissingEventTimes(editor: any): MissingFieldItem[] {
  const items: MissingFieldItem[] = [];
  const sectionPageOrder = getSectionPageOrder(editor, 'JackingEvents', 6);

  const rows = editor?.jackingEvents?.eventRows || [];
  for (const r of rows) {
    if (!r || !r.requiresDateTime) { continue; }

    const hasTime = !!(r.eventDate && (r.eventDate as PtLuxonDate).dateTime);
    const rowKey = (r.id || r.displayOrder || 0);

    if (!hasTime) {
      items.push({
        sectionName: 'Events',
        groupName: null,
        fieldLabel: (r.eventName || 'Event') + ' - Time',
        fieldTemplateId: -100000 - rowKey,
        sectionPageOrder,
        groupDisplayOrder: 1
      } as any);
      continue;
    }

    if (r.requiresDateTimeConfirmation && !r.timeConfirmed) {
      items.push({
        sectionName: 'Events',
        groupName: null,
        fieldLabel: (r.eventName || 'Event') + ' - Time confirmation',
        fieldTemplateId: -200000 - rowKey,
        sectionPageOrder,
        groupDisplayOrder: 1
      } as any);
    }
  }

  return items;
}

function getMissingFinalDataAndPosition(editor: any): MissingFieldItem[] {
  const items: MissingFieldItem[] = [];

  const sectionPageOrder = getSectionPageOrder(editor, 'JackingEvents', 6);
  const fv = editor?.jackingFinalValues;
  if (!fv) { return items; }

  const pushMissing = (fieldLabel: string, stableKey: number) => {
    items.push({
      sectionName: 'Events',
      groupName: 'Final Data',
      fieldLabel,
      fieldTemplateId: -20000 - (sectionPageOrder * 100) - stableKey,
      sectionPageOrder,
      groupDisplayOrder: 2
    } as any);
  };

  const hasTime = (m: any) => m?.timeUtc != null;

  const needsConfirmAndNotConfirmed = (m: any) =>
    m?.timeConfirmed !== undefined && m?.timeConfirmed !== null && m?.timeConfirmed !== true;

  // Final Data
  if (fv.liftPumpsUsed?.depthDeployed == null) {
    pushMissing('Depth Deployed', 1);
  }

  if (fv.finalLegValues && !hasTime(fv.finalLegValues)) {
    pushMissing('Final leg values - Time', 10);
  }

  if (fv.finalPenetrations && !hasTime(fv.finalPenetrations)) {
    pushMissing('Final penetrations - Time', 20);
  }

  if (fv.differenceFromExpected && !hasTime(fv.differenceFromExpected)) {
    pushMissing('Difference from expected - Time', 30);
  }

  if ((editor?.template?.finalValuesTemplate?.hasRpd || fv.finalRpd) && !hasTime(fv.finalRpd)) {
    pushMissing('Final RPD - Time', 35);
  }

  if (fv.finalAirGap) {
    if (!hasTime(fv.finalAirGap)) {
      pushMissing('Final Air Gap - Time', 40);
    } else if (needsConfirmAndNotConfirmed(fv.finalAirGap)) {
      pushMissing('Final Air Gap - Time confirmation', 41);
    }
  }

  // Final Position (labels exactly as original)
  const pos = fv.position;
  if (pos) {
    if (pos.northingCrpPosition == null) {
      pushMissing('CRP Northing Position', 100);
    }

    if (pos.eastingCrpPosition == null) {
      pushMissing('CRP Easting Position', 101);
    }

    if (pos.headingT == null) {
      pushMissing('Heading (T)', 102);
    }

    if (pos.requiredDistanceToAsset == null) {
      pushMissing('Required distance to asset', 103);
    }

    if (pos.actualDistanceToAsset == null) {
      pushMissing('Actual distance to asset', 104);
    }

    // Per-leg Northing/Easting (original loops pos.legs only)
    const legs = Array.isArray(pos.legs) ? pos.legs : [];
    for (const l of legs) {
      const legNo = l?.legNumber;

      if (l?.northing == null) {
        pushMissing(`Leg ${legNo} - Northing`, 200 + (legNo || 0) * 2);
      }

      if (l?.easting == null) {
        pushMissing(`Leg ${legNo} - Easting`, 201 + (legNo || 0) * 2);
      }
    }
  }

  return items;
}

function getMissingLegSummary(editor: any): MissingFieldItem[] {
  const items: MissingFieldItem[] = [];

  const sectionPageOrder = getSectionPageOrder(editor, 'LegSummary', 999);
  const summary = editor?.jackingLegSummary;
  if (!summary) { return items; }

  const pushMissing = (fieldLabel: string, stableKey: number) => {
    items.push({
      sectionName: 'Leg Summary',
      groupName: null,
      fieldLabel,
      fieldTemplateId: -30000 - (sectionPageOrder * 1000) - stableKey,
      sectionPageOrder,
      groupDisplayOrder: 999
    } as any);
  };

  // Use leg layouts if present so we always check full leg set in stable order
  const orderedLegNumbers: number[] = (() => {
    const layouts = editor?.template?.legLayouts || [];
    if (Array.isArray(layouts) && layouts.length) {
      return layouts
        .slice()
        .sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .map((l: any) => l.legNumber);
    }

    const summaryLegs = summary?.legs || [];
    if (Array.isArray(summaryLegs) && summaryLegs.length) {
      return summaryLegs
        .slice()
        .sort((a: any, b: any) => (a.legNumber || 0) - (b.legNumber || 0))
        .map((l: any) => l.legNumber);
    }

    return [];
  })();

  const legs = Array.isArray(summary.legs) ? summary.legs : [];
  const hasValue = (v: any) => v != null && `${v}`.trim() !== '';
  const hasTime = (t: any) => t != null && (t as any).dateTime != null;

  const check = (
    legNo: number,
    labelPrefix: string,
    value: any,
    time: any,
    stableKeyBase: number
  ) => {
    if (!hasValue(value)) {
      pushMissing(`Leg ${legNo} - ${labelPrefix} value`, stableKeyBase + 1);
    }
    if (!hasTime(time)) {
      pushMissing(`Leg ${legNo} - ${labelPrefix} time`, stableKeyBase + 2);
    }
  };

  for (const legNo of orderedLegNumbers) {
    const leg = legs.find((l: any) => l?.legNumber === legNo);

    if (!leg) {
      // original behaviour: missing row => mark all 3 as missing (value + time)
      pushMissing(`Leg ${legNo} - Max pulling load value`, 10000 + legNo * 100 + 1);
      pushMissing(`Leg ${legNo} - Max pulling load time`,  10000 + legNo * 100 + 2);

      pushMissing(`Leg ${legNo} - Free in holes ext value`, 10000 + legNo * 100 + 3);
      pushMissing(`Leg ${legNo} - Free in holes ext time`,  10000 + legNo * 100 + 4);

      pushMissing(`Leg ${legNo} - Clear of seabed ext value`, 10000 + legNo * 100 + 5);
      pushMissing(`Leg ${legNo} - Clear of seabed ext time`,  10000 + legNo * 100 + 6);
      continue;
    }

    check(
      legNo,
      'Max pulling load',
      leg.legMaxPullingLoad,
      leg.legMaxPullingLoadTime,
      10000 + legNo * 100 + 10
    );

    check(
      legNo,
      'Free in holes ext',
      leg.legFreeInHolesExt,
      leg.legFreeInHolesExtTime,
      10000 + legNo * 100 + 20
    );

    check(
      legNo,
      'Clear of seabed ext',
      leg.clearOfSeaBedExt,
      leg.clearOfSeaBedExtTime,
      10000 + legNo * 100 + 30
    );
  }

  return items;
}

function getMissingSignatures(editor: any): MissingFieldItem[] {
  const group = findGroupByComponent(editor, 'Signatures');
  if (!group) { return []; }

  // IMPORTANT: do NOT initialise editor.signatures here (no mutation)
  const s = editor?.signatures || {};

  const missing: MissingFieldItem[] = [];

  const isEmpty = (v: any) => v == null || `${v}`.trim() === '';

  if (isEmpty(s.jackingEngineer1SignerName)) {
    missing.push(createMissingFromGroup(group, 'Jacking Engineer 1 (Name)'));
  }
  if (isEmpty(s.jackingEngineer1Signature)) {
    missing.push(createMissingFromGroup(group, 'Jacking Engineer 1 (Signature)'));
  }
  if (isEmpty(s.jackingEngineer2SignerName)) {
    missing.push(createMissingFromGroup(group, 'Jacking Engineer 2 (Name)'));
  }
  if (isEmpty(s.jackingEngineer2Signature)) {
    missing.push(createMissingFromGroup(group, 'Jacking Engineer 2 (Signature)'));
  }

  return missing;
}



// WEBPACK FOOTER //
// ./src/pages/jacking-activities/v2/helpers/jacking-missing-custom.helper.ts